import { EVENT } from '@/shared/config/event.config';
import { cn } from '@/shared/lib/cn';

interface BrandMarkProps {
  /**
   * Nome do evento. O painel passa o título da parametrização carregada da API; o
   * default cobre as telas fora dela (login), que não têm o documento em mãos.
   */
  title?: string;
  /** Segunda linha. Vazio some com ela, em vez de deixar espaço reservado. */
  subtitle?: string;
  /**
   * `default` na topbar; `muted` quando a marca acompanha um formulário e não deve
   * pesar mais que os rótulos dele; `inverted` sobre fundo escuro; `onBrand` sobre
   * uma superfície pintada com a marca, onde a cor do texto depende de a cor do
   * evento ser clara ou escura.
   */
  tone?: 'default' | 'muted' | 'inverted' | 'onBrand';
  /**
   * `sm` na topbar, onde a marca divide a linha com os controles; `md` quando ela é o
   * cabeçalho de um formulário (login); `lg` quando é o título da tela inteira.
   */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const TITLE_SIZES = {
  sm: 'text-sm font-semibold',
  md: 'text-base leading-tight font-semibold tracking-tight',
  lg: 'text-[1.125rem] leading-tight font-semibold tracking-tight',
} as const;

// Cor de cada tom. Mapa, e não condicional no JSX: `cn` só concatena — não resolve
// conflito entre duas classes de cor, então a escolha tem de ser exclusiva aqui.
const TONE_COLORS = {
  default: { title: 'text-ink', subtitle: 'text-ink-subtle' },
  muted: { title: 'text-ink-muted', subtitle: 'text-ink-subtle' },
  inverted: { title: 'text-white', subtitle: 'text-brand-200/80' },
  onBrand: { title: 'text-on-brand', subtitle: 'text-on-brand/70' },
} as const;

/**
 * Assinatura visual do painel: o nome do evento, sem símbolo.
 *
 * Havia um quadrado com as iniciais "SC" sobre gradiente. Saiu junto com o resto do
 * hardcode do evento: era a única marca escrita no código, e um símbolo fixo
 * contradiz um painel cujo evento vem da parametrização.
 *
 * Sumiu com ele a prop `compact`, que servia para mostrar só o símbolo.
 */
export function BrandMark({ title = EVENT.title, subtitle = EVENT.edition, tone = 'default', size = 'sm', className }: BrandMarkProps) {
  const colors = TONE_COLORS[tone];

  return (
    <div className={cn('flex flex-col leading-tight', size === 'lg' && 'gap-1', className)}>
      <span className={cn(TITLE_SIZES[size], colors.title)}>{title}</span>
      {subtitle ? <span className={cn('text-[0.75rem]', colors.subtitle)}>{subtitle}</span> : null}
    </div>
  );
}
